/**
 * Project operations — list, create, update, delete, checkout, refresh, usage.
 */

import type { ArbiClient, components } from '@arbidocs/client'
import { requireData, requireOk } from '../errors.js'

export type ProjectUsageResponse = components['schemas']['ProjectUsageResponse']
export type ProjectInvoicesResponse = components['schemas']['ProjectInvoicesResponse']
export type UserDailyUsageResponse = components['schemas']['UserDailyUsageResponse']
export type ProjectUpdateRequest = components['schemas']['ProjectUpdateRequest']

export async function listProjects(arbi: ArbiClient) {
  return requireData(await arbi.fetch.GET('/v1/user/projects/'), 'Failed to fetch projects')
}

export async function createProject(arbi: ArbiClient, name: string) {
  return requireData(
    await arbi.fetch.POST('/v1/user/projects/', {
      body: { name },
    }),
    'Failed to create project'
  )
}

/** Update a project's mutable fields (e.g. `name`, `daily_cap_per_user`). */
export async function updateProject(
  arbi: ArbiClient,
  projectExtId: string,
  body: ProjectUpdateRequest
) {
  return requireData(
    await arbi.fetch.PATCH('/v1/user/projects/{project_ext_id}', {
      params: { path: { project_ext_id: projectExtId } },
      body,
    }),
    'Failed to update project'
  )
}

/** Rename a project. Convenience wrapper over {@link updateProject}. */
export async function renameProject(arbi: ArbiClient, projectExtId: string, name: string) {
  return updateProject(arbi, projectExtId, { name })
}

export async function deleteProject(arbi: ArbiClient, projectExtId: string) {
  requireOk(
    await arbi.fetch.DELETE('/v1/user/projects/{project_ext_id}', {
      params: { path: { project_ext_id: projectExtId } },
    }),
    'Failed to delete project'
  )
}

export async function refreshProject(arbi: ArbiClient, projectExtId: string) {
  return requireData(
    await arbi.fetch.POST('/v1/user/projects/{project_ext_id}/refresh', {
      params: { path: { project_ext_id: projectExtId } },
    }),
    'Failed to refresh project'
  )
}

/**
 * AI usage + spend breakdown for a project's billing period. `monthsBack`
 * selects the period: 0 (default) = current, 1 = previous month, etc.
 */
export async function getProjectUsage(
  arbi: ArbiClient,
  projectExtId: string,
  monthsBack?: number
): Promise<ProjectUsageResponse> {
  return requireData(
    await arbi.fetch.GET('/v1/user/projects/{project_ext_id}/usage', {
      params: {
        path: { project_ext_id: projectExtId },
        query: monthsBack === undefined ? undefined : { months_back: monthsBack },
      },
    }),
    'Failed to fetch project usage'
  )
}

/** List the Stripe invoices for a project's subscription. */
export async function getProjectInvoices(
  arbi: ArbiClient,
  projectExtId: string
): Promise<ProjectInvoicesResponse> {
  return requireData(
    await arbi.fetch.GET('/v1/user/projects/{project_ext_id}/invoices', {
      params: { path: { project_ext_id: projectExtId } },
    }),
    'Failed to fetch project invoices'
  )
}

/**
 * Today's deployment-wide credit spend for the current user — the number the
 * per-user daily cap is enforced against. Pair with a project's
 * `daily_cap_per_user` to render a "credits today" indicator.
 */
export async function getUserUsageToday(arbi: ArbiClient): Promise<UserDailyUsageResponse> {
  return requireData(await arbi.fetch.GET('/v1/user/me/usage/today'), 'Failed to fetch today usage')
}
